import { Guid } from "devextreme-vue/common";
import chatIcon from "@/assets/icons/assistantIcon.svg";
import { useSessionStorage } from "@vueuse/core";
import aiAssistantService from "../../services/aiAssistant.service";
import { i18nInstance } from "@/i18n";

// The AI assistant is branded differently per market: "Lotse" for the German
// audience, "Vinny" for the Dutch one. Resolve from the active i18n locale.
const supportAgentNamesByLocale = {
  nl: "Vinny",
  de: "Lotse",
};

const resolveSupportAgentName = () => {
  const locale = i18nInstance?.global?.locale?.value;
  return supportAgentNamesByLocale[locale] ?? "Vinny";
};

const persistedState = useSessionStorage("chat", {
  currentConversationId: "",
  conversationOrder: [],
  userAnsweredWelcomeMessage: false,
});

const makeWelcomeMessage = (conversationId, author, welcomeText) => ({
  id: new Guid().toString(),
  timestamp: new Date(),
  author: author,
  text: welcomeText,
  conversationId: conversationId,
});

const createAssistantPlaceholderMessage = (state, assistantId) => ({
  id: assistantId,
  timestamp: new Date(),
  author: state.supportAgent,
  text: "",
  conversationId: state.currentConversationId,
  attachments: [],
});

const pickLastThreeConversationsPayload = (
  state,
  userAnsweredWelcomeMessage,
) => {
  const order = (state.conversationOrder || []).slice(0, 3);

  return {
    currentConversationId: state.currentConversationId || order[0] || "",
    conversationOrder: order,
    userAnsweredWelcomeMessage: userAnsweredWelcomeMessage,
  };
};

const persistFromState = (state, userAnsweredWelcomeMessage) => {
  persistedState.value = pickLastThreeConversationsPayload(
    state,
    userAnsweredWelcomeMessage,
  );
};

const state = () => ({
  showChat: false,
  aiAssistantAccessToken: {
    accessToken: "",
    expirationTime: null,
  },
  settings: null,
  supportAgent: {
    id: "assistant",
    // Safe default before i18n is ready; `setSupportAgentName` resolves the
    // locale-specific brand name at chat init, before any message is rendered.
    name: "Vinny",
    avatarUrl: chatIcon,
  },
  currentUser: {
    id: "user",
    name: "User",
  },
  currentConversationId: "",
  // order of conversations (newest first), max 3 since last 3 conversations should be visible in chat
  conversationOrder: [],
  // messages grouped by conversationId, key = conversationId, value = array of messages
  messagesByConversation: {},
  typingUsers: [],
  isHistoryPopupVisible: false,
  chatHistoryMessages: [],
  messagesFromSelectedConversation: [],
  currentConversationIdForHistory: "",
  streamId: "",
  assistantMessageId: null,
});

const getters = {
  visibleMessages(state) {
    const idsChronological = (state.conversationOrder || [])
      .slice(0, 3)
      .reverse();
    const merged = [];

    for (const conversationId of idsChronological) {
      const arr = state.messagesByConversation[conversationId] || [];
      merged.push(...arr);
    }
    return merged;
  },
};

const actions = {
  async initChatFromSession({ commit, dispatch, state }) {
    commit("setSupportAgentName");
    commit("hydrateFromSession", persistedState.value);

    if (Object.keys(state.messagesByConversation).length === 0) {
      const conversationIds = persistedState.value?.conversationOrder ?? [];

      // if there is at least one started conversation, retrieve messages
      if (persistedState.value?.userAnsweredWelcomeMessage) {
        if (conversationIds.length)
          await aiAssistantService
            .getMessagesByConversations(conversationIds)
            .catch(() => {
              dispatch("resetChat");
              dispatch("startNewConversation");
            });
      } else {
        // last conversation shouldn't be retrieved since it contains only the welcome message
        const remainingConversations = conversationIds.filter(
          (c) => c !== state.currentConversationId,
        );
        persistedState.value.conversationOrder = remainingConversations;

        if (remainingConversations.length)
          await aiAssistantService
            .getMessagesByConversations(remainingConversations)
            .catch(() => {
              dispatch("resetChat");
            });

        dispatch("startNewConversation");
      }
    }
  },
  startNewConversation({ commit, state }) {
    commit("setSupportAgentName");
    commit("startNewConversation");
    commit("setTypingUsers", []);
    persistFromState(state, false);
  },
  // for errors only
  addAssistantMessage({ commit, state }, content) {
    let id = new Guid().toString();
    commit("setAssistantMessageId", id);

    const message = {
      id: id,
      timestamp: new Date(),
      author: state.supportAgent,
      text: content,
      conversationId: state.currentConversationId,
    };

    commit("addMessageToCurrentConversation", message);
    persistFromState(state, true);
    commit("setTypingUsers", []);
  },
  insertUserMessage({ commit, state }, message) {
    commit("addMessageToCurrentConversation", message);
    persistFromState(state, true);
    commit("setTypingUsers", [state.supportAgent]);
    return message;
  },
  insertAssistantPlaceholder({ commit, state }, assistantId) {
    const message = createAssistantPlaceholderMessage(state, assistantId);

    commit("setAssistantMessageId", assistantId);
    commit("addMessageToCurrentConversation", message);
    persistFromState(state, true);
    commit("setTypingUsers", []);

    return message;
  },
  updateAssistantMessageText({ commit, state }, { assistantMessageId, text }) {
    commit("updateAssistantMessageText", {
      assistantMessageId,
      text,
    });
    persistFromState(state, true);
  },
  addAssistantGeneratedFile({ commit, state }, { assistantMessageId, file }) {
    commit("addGeneratedFileToAssistantMessage", { assistantMessageId, file });
    persistFromState(state, true);
  },
  removeMessageById({ commit, state }, messageId) {
    commit("removeMessageById", messageId);
    persistFromState(state, true);
  },
  clearStreamingState({ commit }) {
    commit("clearStreamingState");
  },
  resetChat({ commit }) {
    commit("resetChatState");
    persistedState.value = {
      currentConversationId: "",
      conversationOrder: [],
      userAnsweredWelcomeMessage: false,
    };
  },
  async setAIAssistantAccessToken({ commit, dispatch }, token, errorText) {
    commit("setAIAssistantAccessToken", token);
    let sessionResponse = await aiAssistantService.setSession();

    if (sessionResponse.status === 401) {
      const response = await aiAssistantService
        .getAIAssistantAccessToken()
        .catch((error) => {
          throw error || new Error(errorText);
        });

      if (response.status === 200) {
        commit("setAIAssistantAccessToken", response.data);
        await aiAssistantService.setSession().catch((error) => {
          throw error || new Error(errorText);
        });
      }
    }
  },
};

const mutations = {
  updateChatHistoryMessages(state, val) {
    state.chatHistoryMessages = val.map((v) => ({
      conversationId: v.conversationId,
      conversationTopic: v.conversationTopic,
      userId: v.userId,
      conversationStartedAt: v.conversationStartedAt,
      shortTopic:
        v.conversationTopic.length > 100
          ? v.conversationTopic.slice(0, 100) + "..."
          : v.conversationTopic,
    }));
  },
  setMessagesFromSelectedConversation(state, messages) {
    if (!messages || messages.length === 0)
      state.messagesFromSelectedConversation.length = 0;

    state.messagesFromSelectedConversation = messages.map((message) => ({
      id: message.messageId,
      author:
        message.role === "assistant" ? state.supportAgent : state.currentUser,
      conversationId: message.conversationId,
      timestamp: message.timestamp,
      text: message.content,
      attachments:
        message.attachments && message.attachments.length
          ? message.attachments.map((attachment) => ({
              attachmentId: attachment.attachmentId,
              name: attachment.name,
              size: attachment.size,
            }))
          : [],
      attachmentIds:
        message.attachmentIds && message.attachmentIds.length ? [...message.attachmentIds] : [],
    }));
  },
  updateIsHistoryPopupVisible(state, val) {
    state.isHistoryPopupVisible = val;
  },
  updateShowChat(state, val) {
    state.showChat = val;
  },
  setTypingUsers(state, users) {
    state.typingUsers = [...users];
  },
  hydrateFromSession(state, persisted) {
    if (!persisted) return;

    state.currentConversationId = persisted.currentConversationId || "";
    state.conversationOrder = Array.isArray(persisted.conversationOrder)
      ? persisted.conversationOrder
      : [];

    // if there is no active conversation but conversations order is not empty, set first conversation as active
    if (!state.currentConversationId && state.conversationOrder.length > 0) {
      state.currentConversationId = state.conversationOrder[0];
    }
  },
  startNewConversation(state) {
    const conversationId = new Guid().toString();
    state.currentConversationId = conversationId;
    state.messagesByConversation[conversationId] = [
      makeWelcomeMessage(
        conversationId,
        state.supportAgent,
        state.settings.introText,
      ),
    ];

    state.conversationOrder = [
      conversationId,
      ...state.conversationOrder.filter((x) => x !== conversationId),
    ].slice(0, 3);

    // delete dropped conversations
    for (const key of Object.keys(state.messagesByConversation)) {
      if (!state.conversationOrder.includes(key))
        delete state.messagesByConversation[key];
    }
  },
  addMessageToCurrentConversation(state, message) {
    const conversationId = state.currentConversationId;

    if (!conversationId) return;

    if (!state.messagesByConversation[conversationId])
      state.messagesByConversation[conversationId] = [];

    state.messagesByConversation[conversationId].push(message);
  },
  setMessagesPerConversation(state, conversations) {
    for (const conversationId in conversations || {}) {
      const messages = conversations[conversationId] ?? [];

      state.messagesByConversation[conversationId] = messages.length
        ? messages.map((m) => ({
            id: m.messageId,
            author: m.role === "user" ? state.currentUser : state.supportAgent,
            conversationId: m.conversationId,
            timestamp: m.timestamp,
            text: m.content,
            attachments:
              m.attachments && m.attachments.length
                ? m.attachments.map((attachment) => ({
                    attachmentId: attachment.attachmentId,
                    name: attachment.name,
                    size: attachment.size,
                  }))
                : [],
            attachmentIds:
              m.attachmentIds && m.attachmentIds.length
                ? [...m.attachmentIds]
                : [],
          }))
        : [];
    }
  },
  updateAssistantMessageText(state, { assistantMessageId, text }) {
    if (!assistantMessageId) return;

    for (const conversationId of Object.keys(state.messagesByConversation)) {
      const messages = state.messagesByConversation[conversationId];

      if (!Array.isArray(messages)) continue;

      const index = messages.findIndex(
        (message) => message.id === assistantMessageId,
      );
      if (index === -1) continue;

      messages.splice(index, 1, {
        ...messages[index],
        text,
      });
      return;
    }
  },
  addGeneratedFileToAssistantMessage(state, { assistantMessageId, file }) {
    if (!assistantMessageId || !file) return;

    for (const conversationId of Object.keys(state.messagesByConversation)) {
      const messages = state.messagesByConversation[conversationId];

      if (!Array.isArray(messages)) continue;

      const index = messages.findIndex(
        (message) => message.id === assistantMessageId,
      );
      if (index === -1) continue;

      const existingAttachments = messages[index].attachments || [];

      if (existingAttachments.some((a) => a.attachmentId === file.attachmentId))
        return;

      // DxChat renders the native "attachments" array; the generated file is stored in our blob
      // storage under attachmentId and downloaded through the existing downloadAttachment flow.
      const attachment = {
        attachmentId: file.attachmentId,
        name: file.name,
        size: (file.sizeInKb || 0) * 1024,
      };

      messages.splice(index, 1, {
        ...messages[index],
        attachments: [...existingAttachments, attachment],
      });
      return;
    }
  },
  removeMessageById(state, messageId) {
    if (!messageId) return;

    for (const conversationId of Object.keys(state.messagesByConversation)) {
      const messages = state.messagesByConversation[conversationId];

      if (!Array.isArray(messages)) continue;

      const index = messages.findIndex((message) => message.id === messageId);
      if (index === -1) continue;

      messages.splice(index, 1);
      return;
    }
  },
  clearStreamingState(state) {
    state.streamId = "";
    state.assistantMessageId = null;
    state.typingUsers = [];
  },
  resetChatState(state) {
    state.currentConversationId = "";
    state.conversationOrder.length = 0;
    state.messagesByConversation = {};
    state.chatHistoryMessages = [];
    state.messagesFromSelectedConversation = [];
    state.streamId = "";
    state.assistantMessageId = null;
    state.typingUsers = [];
  },
  setSupportAgentName(state) {
    state.supportAgent.name = resolveSupportAgentName();
  },
  fillSettings(state, val) {
    state.settings = val;
  },
  setAIAssistantAccessToken(state, val) {
    state.aiAssistantAccessToken.accessToken = val.accessToken;
    state.aiAssistantAccessToken.expirationTime = val.expirationTime;
  },

  setCurrentConversationIdForHistory(state, val) {
    state.currentConversationIdForHistory = val;
  },

  continueChatFromHistory(state) {
    const conversationId = state.currentConversationIdForHistory;

    if (!conversationId) return;

    const restoredMessages = (state.messagesFromSelectedConversation ?? []).map(
      (message) => ({
        ...message,
      }),
    );

    state.conversationOrder = [
      conversationId,
      ...state.conversationOrder.filter((x) => x !== conversationId),
    ].slice(0, 3);

    state.currentConversationId = conversationId;
    state.messagesByConversation[conversationId] = restoredMessages;

    for (const key of Object.keys(state.messagesByConversation)) {
      if (!state.conversationOrder.includes(key))
        delete state.messagesByConversation[key];
    }

    persistFromState(state, true);
  },
  setStreamId(state, val) {
    state.streamId = val;
  },
  setAssistantMessageId(state, val) {
    state.assistantMessageId = val;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
